import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Form, Space, Tree } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { DataNode } from "antd/es/tree";
import type { Key } from "react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    Table as ZhTable,
    type TableColumnProps,
    Form as ZhFilterForm,
    Button as ZhFilterButton,
    Input as ZhFilterInput
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";

export interface DepartmentNode {
    id: number | string;
    data?: { name?: string };
    childNodes?: DepartmentNode[];
}

export interface DepartmentTreeResult {
    rootNode?: DepartmentNode[];
}

export interface OrganizationPerson {
    id?: number | string;
    person?: {
        id?: number | string;
        employeeId?: string;
        name?: string;
        position?: string;
        mobile?: string;
        mail?: string;
    };
    depts?: Array<Array<{ name?: string }>>;
}

export interface OrganizationPersonPageResult {
    list?: OrganizationPerson[];
    total?: number;
}

export interface OrganizationPersonQuery {
    page: number;
    size: number;
    deptId: number;
    param?: string;
}

type OrganizationPersonFilterValues = {
    param?: string;
};

export interface OrganizationalStructurePageProps {
    loadDepartmentTree?: () => Promise<DepartmentTreeResult>;
    loadPeople?: (query: OrganizationPersonQuery) => Promise<OrganizationPersonPageResult>;
    permissions?: string[];
}

const defaultQuery: OrganizationPersonQuery = { page: 1, size: 10, deptId: 1 };

/** 复用旧 Vue `/admin/dept/tree` 接口。 */
export async function loadDepartmentTree(): Promise<DepartmentTreeResult> {
    const response = await request.service.get<DepartmentTreeResult>("/admin/dept/tree");
    return response.data;
}

/** 复用旧 Vue `/admin/person/paging` 接口，默认查询根部门人员。 */
export async function loadOrganizationPeople(query: OrganizationPersonQuery): Promise<OrganizationPersonPageResult> {
    const response = await request.service.get<OrganizationPersonPageResult>("/admin/person/paging", {
        params: query
    });
    return response.data;
}

const getTreeData = (nodes: DepartmentNode[]): DataNode[] =>
    nodes.map(node => ({
        key: String(node.id),
        title: node.data?.name || "未命名部门",
        children: getTreeData(node.childNodes || [])
    }));

/** 复刻旧 Vue 组织树的开合文件夹图标，避免依赖未直接安装的图标包。 */
function OrganizationFolderIcon({ open }: { open: boolean }) {
    return (
        <svg aria-hidden="true" fill="currentColor" height="20" viewBox="0 0 24 24" width="20">
            <path
                d={
                    open
                        ? "M3 6.5A2.5 2.5 0 0 1 5.5 4h4l2 2H19a2 2 0 0 1 2 2v1H6.1a2 2 0 0 0-1.9 1.37L3 14V6.5Zm2.1 4H22l-2.3 7A2.2 2.2 0 0 1 17.6 19H3.4a1.4 1.4 0 0 1-1.33-1.84l2.08-6.24a1 1 0 0 1 .95-.42Z"
                        : "M3 6.5A2.5 2.5 0 0 1 5.5 4h4l2 2H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6.5Z"
                }
            />
        </svg>
    );
}

const getDepartmentNames = (depts: OrganizationPerson["depts"] = []) =>
    depts
        .map(path => path[path.length - 1]?.name)
        .filter(Boolean)
        .join("；") || DEFAULT_VALUE;

const formatPersonValue = (value: unknown): string =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

/** 旧 Vue PersonVo 将编辑所需员工 ID 放在 person.id，兼容迁移期间的顶层 ID。 */
const getPersonId = (person: OrganizationPerson): number | string | undefined => person.person?.id ?? person.id;

/** 系统管理-组织架构页面，覆盖 Vue 树和人员分页主流程。 */
export function OrganizationalStructurePage({
    loadDepartmentTree: loadTree = loadDepartmentTree,
    loadPeople = loadOrganizationPeople,
    permissions
}: OrganizationalStructurePageProps) {
    const [tree, setTree] = useState<DepartmentNode[]>([]);
    const [people, setPeople] = useState<OrganizationPerson[]>([]);
    const [total, setTotal] = useState(0);
    const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
    const [collapsed, setCollapsed] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [query, setQuery] = useState<OrganizationPersonQuery>(defaultQuery);
    const [filterForm] = Form.useForm<OrganizationPersonFilterValues>();
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeBanButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    // 保留 Vue v-auth 的特殊权限：封禁/提示权限会保留按钮，封禁权限同时禁用按钮。
    const canEdit = currentPermissions.some(permission =>
        ["ROLE_EMPLOYEE_W", "ROLE_BUTTON_BAN", "ROLE_BUTTON_TIP"].includes(permission)
    );
    const editDisabled = permissions ? currentPermissions.includes("ROLE_BUTTON_BAN") : storeBanButton;

    const treeData = useMemo(() => getTreeData(tree), [tree]);

    useEffect(() => {
        let disposed = false;
        void loadTree()
            .then(departmentResult => {
                if (!disposed) {
                    const rootNode = departmentResult.rootNode || [];
                    setTree(rootNode);
                    setExpandedKeys(rootNode.map(node => String(node.id)));
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("组织架构加载失败");
                    setTree([]);
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadTree]);

    useEffect(() => {
        let disposed = false;
        void loadPeople(query)
            .then(result => {
                if (!disposed) {
                    setPeople(result.list || []);
                    setTotal(result.total || 0);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("人员列表加载失败");
                    setPeople([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadPeople, query]);

    // 将旧 Vue ProTable 的人员关键词筛选转换为人员分页接口参数。
    const submitFilters = ({ param }: OrganizationPersonFilterValues) => {
        setErrorMessage(null);
        setQuery(currentQuery => ({
            page: 1,
            size: 10,
            deptId: currentQuery.deptId,
            ...(param ? { param } : {})
        }));
    };

    // 用 zhihao-ui-react 列契约承接 Vue 组织成员的嵌套人员字段和权限操作。
    const columns: TableColumnProps<OrganizationPerson>[] = [
        {
            key: "employeeId",
            label: "工号",
            width: 140,
            prop: "employeeId",
            render: ({ row }) => formatPersonValue(row.person?.employeeId)
        },
        {
            key: "name",
            label: "姓名",
            minWidth: 118,
            prop: "name",
            render: ({ row }) => formatPersonValue(row.person?.name)
        },
        {
            key: "depts",
            label: "部门",
            minWidth: 132,
            prop: "depts",
            render: ({ row }) => getDepartmentNames(row.depts)
        },
        {
            key: "position",
            label: "职务",
            minWidth: 262,
            prop: "position",
            render: ({ row }) => formatPersonValue(row.person?.position)
        },
        {
            key: "mobile",
            label: "手机号",
            width: 160,
            prop: "mobile",
            render: ({ row }) => formatPersonValue(row.person?.mobile)
        },
        {
            key: "mail",
            label: "邮箱",
            width: 200,
            prop: "mail",
            render: ({ row }) => formatPersonValue(row.person?.mail)
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            // Vue 固定操作列宽度为 100px，保持组织架构列表的操作区和旧版一致。
            width: 100,
            prop: "operation",
            render: ({ row }: { row: OrganizationPerson }) => {
                const personId = getPersonId(row);
                return canEdit && personId != null ? (
                    <Space>
                        <Button
                            aria-label={`编辑员工${row.person?.name || personId}`}
                            disabled={editDisabled}
                            onClick={() => {
                                // 两套路由都沿用 Vue 路径：系统管理使用 query，平台管理使用 path 参数。
                                const editPath = location.pathname.startsWith("/manageCenter")
                                    ? `/manageCenter/systemManage/organizationalStructure/edit/${personId}`
                                    : `/systemMangement/systemManage/organizationalStructure/edit?id=${personId}`;
                                void navigate(editPath);
                            }}
                            type="link"
                        >
                            编辑
                        </Button>
                    </Space>
                ) : null;
            }
        }
    ];

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="organizational-structure-page">
            {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
            <div className="organizational-structure flex min-h-0 flex-1">
                <aside
                    className="dept-list relative mr-[30px] h-[calc(var(--zh-layout-main-height)-56px)] w-[280px] shrink-0 overflow-auto border-r border-[rgb(98_109_131_/_20%)] pr-5"
                    aria-label="department-tree"
                >
                    <div className="dept-list-header mb-5 flex w-[260px] items-center justify-between">
                        <div className="title text-[18px] font-normal text-black">组织架构</div>
                        <Button
                            className="collapse-all h-auto px-0 text-xs font-normal text-[#626d83]"
                            size="small"
                            type="text"
                            onClick={() => {
                                const nextCollapsed = !collapsed;
                                setCollapsed(nextCollapsed);
                                setExpandedKeys(nextCollapsed ? [] : tree.map(node => String(node.id)));
                            }}
                        >
                            {collapsed ? "展开全部" : "收起全部"}
                        </Button>
                    </div>
                    <Tree
                        blockNode
                        className="w-[260px] overflow-x-auto rounded"
                        expandedKeys={expandedKeys}
                        icon={({ expanded }) => (
                            <span className="text-[rgba(23,119,255,0.7)]">
                                <OrganizationFolderIcon open={Boolean(expanded)} />
                            </span>
                        )}
                        showIcon
                        treeData={treeData}
                        onExpand={keys => setExpandedKeys(keys)}
                        onSelect={keys => {
                            const nextId = keys[0];
                            if (nextId == null) return;
                            const deptId = Number(nextId);
                            setQuery(currentQuery => ({ ...currentQuery, page: 1, deptId }));
                        }}
                    />
                </aside>
                <div className="organizational-user min-w-0 flex-1 overflow-auto">
                    <ZhTable<OrganizationPerson>
                        data={people}
                        // 组织架构只有一个编辑动作，按视觉基线保留 82px 操作列，不使用多动作列的通用扩容规则。
                        columns={columns}
                        pagination={false}
                        rowKey={record => record.id || record.person?.employeeId || "person"}
                        isShowResetBtn={false}
                        isShowSearchBtn={false}
                        tableHeader={
                            <div className="admin-table-composed-header">
                                <ZhFilterForm
                                    className="admin-table-filter-form mb-4"
                                    form={filterForm}
                                    layout="inline"
                                    onFinish={values => submitFilters(values)}
                                >
                                    <ZhFilterForm.Item noStyle name="param">
                                        <ZhFilterInput
                                            className="w-[322px]"
                                            maxLength={32}
                                            placeholder="姓名、手机号或邮箱"
                                        />
                                    </ZhFilterForm.Item>
                                    <ZhFilterButton
                                        htmlType="submit"
                                        icon={<TableSearchIcon name="search" />}
                                        type="primary"
                                    >
                                        搜索
                                    </ZhFilterButton>
                                </ZhFilterForm>
                            </div>
                        }
                        append={
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    current={query.page}
                                    locale={{ jump_to: "转到" }}
                                    pageSize={query.size}
                                    total={total}
                                    pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                    showSizeChanger
                                    // 沿用 Vue 分页参数，切换页大小时回到第 1 页并保留部门。
                                    onChange={(page, pageSize) =>
                                        setQuery(currentQuery => ({
                                            ...currentQuery,
                                            page: pageSize !== currentQuery.size ? 1 : page,
                                            size: pageSize
                                        }))
                                    }
                                />
                            </div>
                        }
                    />
                </div>
            </div>
        </section>
    );
}

export default OrganizationalStructurePage;
